import { Children, isValidElement, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { Link, matchPath, useLocation } from 'react-router'

export const Tabs = ({ children, className, label = 'Sections' }) => {
  const location = useLocation()
  const tabs = Children.toArray(children).filter(isValidElement)
  const tabListRef = useRef(null)
  const [indicator, setIndicator] = useState(null)
  const [indicatorReady, setIndicatorReady] = useState(false)

  useEffect(() => {
    const tabList = tabListRef.current

    if (!tabList) {
      return undefined
    }

    let cancelled = false

    const measureIndicator = () => {
      const activeTab = tabList.querySelector('[aria-current="page"]')

      if (!activeTab) {
        setIndicator(null)
        return
      }

      setIndicator({ left: activeTab.offsetLeft, width: activeTab.offsetWidth })
    }

    measureIndicator()
    const readyFrame = requestAnimationFrame(() => setIndicatorReady(true))
    window.addEventListener('resize', measureIndicator)

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(measureIndicator)

    resizeObserver?.observe(tabList)

    document.fonts?.ready.then(() => {
      if (!cancelled) measureIndicator()
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(readyFrame)
      window.removeEventListener('resize', measureIndicator)
      resizeObserver?.disconnect()
    }
  }, [location.pathname, tabs.length])

  return (
    <nav
      aria-label={label}
      className={cx('tw:relative tw:mx-auto tw:my-6 tw:flex tw:w-fit tw:rounded-card tw:bg-surface-subtle tw:p-1 tw:compact:w-full', className)}
      ref={tabListRef}
    >
      <span
        aria-hidden="true"
        className="ds-tabs-indicator"
        data-ready={indicatorReady ? 'true' : undefined}
        style={indicator
          ? {
            '--ds-tabs-left': `${indicator.left}px`,
            '--ds-tabs-width': `${indicator.width}px`,
            opacity: 1,
          }
          : { opacity: 0 }}
      />
      {tabs.map(tab => {
        const to = tab.props.to
        const isActive = Boolean(matchPath({ end: true, path: to }, location.pathname))

        return (
          <Link
            aria-current={isActive ? 'page' : undefined}
            className={cx(
              'tw:relative tw:z-10 tw:flex tw:min-h-10 tw:items-center tw:justify-center tw:rounded-input tw:px-4 tw:text-base tw:text-foreground-muted tw:no-underline tw:hover:text-foreground-strong tw:hover:no-underline tw:compact:flex-1',
              'tw:transition-colors tw:duration-fast tw:ease-standard',
              isActive && 'tw:text-foreground-strong',
            )}
            key={tab.key}
            to={to}
          >
            {tab.props.tab}
          </Link>
        )
      })}
    </nav>
  )
}
